/**
 * CodeBlock Component
 * 
 * Renders code blocks with optional syntax highlighting for SQL.
 */

import React from 'react';
import { highlightSql } from '@/shared/lib/markdown';

interface CodeBlockProps {
    /** Code content */
    children: React.ReactNode;
    /** Language class (e.g., "language-sql") */
    className?: string;
}

/**
 * Code block with syntax highlighting support.
 * Currently supports SQL syntax highlighting.
 */
export const CodeBlock: React.FC<CodeBlockProps> = ({ children, className }) => {
    const isInline = !className;
    const content = String(children);
    const isSql = className?.includes('language-sql');

    // Inline code
    if (isInline) {
        return (
            <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-sm font-mono text-slate-800 dark:text-slate-200">
                {children}
            </code>
        );
    }

    // SQL code block with syntax highlighting
    if (isSql) {
        return (
            <code className={`${className} block bg-slate-900 dark:bg-slate-950 p-4 rounded-lg overflow-x-auto`}>
                <pre className="text-sm font-mono leading-relaxed">
                    {highlightSql(content)}
                </pre>
            </code>
        );
    }

    // Generic code block
    return (
        <code className={`${className} block bg-slate-900 dark:bg-slate-950 p-4 rounded-lg overflow-x-auto`}>
            <pre className="text-sm text-accent font-mono leading-relaxed">
                {children}
            </pre>
        </code>
    );
};
